/**
 * Module 6C.4 Layer 1 — Billing client helper.
 *
 * Single source of truth for fetching usage + formatting plan limits
 * for display. Pairs with `components/PlanBadge.jsx` (sidebar) and
 * `components/UpgradeModal.jsx` (402 catcher).
 *
 * The 402 interceptor lives here too — Axios global response handler
 * that extracts the structured detail payload and dispatches a custom
 * window event. Any component (typically the App-shell <UpgradeModal/>)
 * that listens for `lead_studio:upgrade_required` can pop the prompt
 * without each call site having to try/catch.
 */
import { api } from "@/lib/api";


// ───────── API ─────────

export async function fetchBillingUsage() {
  try {
    const { data } = await api.get("/billing/usage");
    return data;
  } catch {
    return null;
  }
}

export async function fetchBillingPlans() {
  try {
    const { data } = await api.get("/billing/plans");
    return data?.plans || null;
  } catch {
    return null;
  }
}

export async function cancelSubscription() {
  const { data } = await api.post("/billing/cancel");
  return data;
}


// ───────── Proactive upgrade trigger ─────────

/**
 * Fires the same `lead_studio:upgrade_required` event that the 402
 * interceptor fires, but with `limit_type: "proactive"` so `UpgradeModal`
 * can render happy-path copy instead of a limit-hit warning. The event
 * shape mirrors the 402 detail so all downstream listeners (PlanBadge
 * refresh, modal render, telemetry) stay unchanged.
 */
export function openUpgradeProactive({ currentPlan = "trial", label = "Trial" } = {}) {
  try {
    window.dispatchEvent(new CustomEvent("lead_studio:upgrade_required", {
      detail: {
        error:        "plan_limit_exceeded",  // keeps listeners' shape check happy
        limit_type:   "proactive",
        current_plan: currentPlan,
        label,
        upgrade_to:   ["studio", "pro"],
        used:         0,
        limit:        null,
      },
    }));
  } catch { /* no-op */ }
}


// ───────── Formatting ─────────

const WINDOW_LABELS = {
  calendar_month: "this month",
  rolling_year:   "this year",
  live:           "active",
};

export function formatLimit(used, limit, window) {
  if (limit == null) return "Unlimited";
  const w = WINDOW_LABELS[window] || "";
  return `${used} / ${limit}${w ? ` ${w}` : ""}`;
}

export function planTone(planKey) {
  // Used to color the plan badge in the sidebar. Trial is celebratory,
  // grandfathered is reassuring, paid is brand-primary, free is muted.
  switch (planKey) {
    case "trial":         return { bg: "var(--brand-primary)", fg: "#fff" };
    case "grandfathered": return { bg: "var(--brand-paper-soft)", fg: "var(--brand-ink)" };
    case "studio":
    case "pro":           return { bg: "var(--brand-primary)", fg: "#fff" };
    case "free":
    default:              return { bg: "var(--brand-paper-soft)", fg: "var(--brand-muted)" };
  }
}


// ───────── Trial countdown ─────────

export function trialDaysLeft(usage) {
  if (!usage || usage.plan !== "trial" || !usage.trial_ends_at) return null;
  const ends = new Date(usage.trial_ends_at).getTime();
  const ms = ends - Date.now();
  if (ms <= 0) return 0;
  return Math.max(1, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}


// ───────── Trial nudge banner ─────────

export function nudgeCopy(state) {
  // Backend (`billing_snapshot.trial_nudge`) is the single source of
  // truth — soft on day 10, critical on day 13. Pure lazy compute, no
  // cron. Returning null means no banner.
  if (!state || !state.trial_nudge) return null;
  const daysLeft = trialDaysLeft(state);
  if (state.trial_nudge === "critical") {
    return {
      tone: "critical",
      text: `${daysLeft} days left in your trial. Choose a plan to stay unlimited — save ~17% with yearly billing.`,
    };
  }
  if (state.trial_nudge === "soft") {
    return {
      tone: "soft",
      text: `${daysLeft} days left in your trial — add a plan to keep unlimited leads & projects.`,
    };
  }
  return null;
}


// ───────── Razorpay Checkout launch ─────────

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error("razorpay_script_load_failed"));
    document.body.appendChild(s);
  });
}

/**
 * Open Razorpay Checkout for a subscription.
 *
 * @param {{tier:'studio'|'pro', cadence:'monthly'|'yearly'}} opts
 * @returns {Promise<{razorpay_payment_id, razorpay_subscription_id, razorpay_signature}|null>}
 *
 * IMPORTANT: the resolved callback payload is for UX ONLY. The actual
 * plan flip happens server-side when Razorpay's webhook hits
 * /api/billing/webhook with subscription.authenticated. Callers MUST
 * poll /api/billing/usage until plan flips (don't trust client state).
 */
export async function openRazorpayCheckout({ tier, cadence }) {
  await loadRazorpayScript();
  const { data } = await api.post("/billing/subscribe", { tier, cadence });
  return new Promise((resolve) => {
    const rzp = new window.Razorpay({
      key:             data.key_id,
      subscription_id: data.subscription_id,
      name:            "Lead Studio",
      description:     `${tier === "pro" ? "Pro" : "Studio"} · ${cadence === "yearly" ? "Yearly" : "Monthly"}`,
      prefill:         data.prefill || {},
      // Yearly plans exceed the ₹15K UPI AutoPay cap. Per spec, we hide
      // UPI for yearly and show eMandate + tokenized card. Monthly keeps
      // all methods incl. UPI.
      method: cadence === "yearly"
        ? { upi: false, card: true, netbanking: true, wallet: false }
        : undefined,
      theme: { color: "#0e3030" },
      handler: function (response) {
        // UX-only — webhook does the real work. Caller polls /usage.
        resolve(response);
      },
      modal: { ondismiss: () => resolve(null) },
    });
    rzp.open();
  });
}

/**
 * Wait for the studio's plan to flip to {tier} via the webhook. Polls
 * /billing/usage every 2s (max ~30s). If the webhook is delayed or has
 * not arrived by the deadline, we make ONE pull-side reconcile call to
 * /billing/reconcile — that endpoint fetches the subscription state
 * directly from Razorpay and applies the same flip the webhook would.
 *
 * Resolves to the final snapshot (success) or null (still pending —
 * Razorpay says subscription not yet authenticated). The polling exists
 * purely so the FE can unblock the spinner once the plan lands — it
 * never decides plan state itself.
 */
export async function waitForActivation(tier, { timeoutMs = 30000, intervalMs = 2000 } = {}) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const snap = await fetchBillingUsage();
    if (snap && snap.plan === tier && snap.stored_status === "active") {
      return snap;
    }
    await new Promise(r => setTimeout(r, intervalMs));
  }
  // Webhook hasn't landed in time — pull-side reconcile fallback.
  // Same code path as the webhook (server-side), so still secure +
  // idempotent. If Razorpay confirms the subscription is active, the
  // /reconcile response includes the fresh snapshot.
  try {
    const { data } = await api.post("/billing/reconcile");
    if (data?.snapshot?.plan === tier && data.snapshot.stored_status === "active") {
      return data.snapshot;
    }
  } catch (_e) {
    // 404 (no pending sub) / 502 / 503 → fall through to timeout null.
    void _e;
  }
  return null;
}


// ───────── 402 interceptor (registered once on app boot) ─────────

let interceptorRegistered = false;

export function registerUpgradeInterceptor() {
  if (interceptorRegistered) return;
  interceptorRegistered = true;
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      // Look for the structured 6C.4 detail shape. We don't intercept
      // generic 402s from outside billing (there are none today; this
      // guard-rails against future external integrations that might
      // borrow the code).
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 402 && detail && detail.error === "plan_limit_exceeded") {
        try {
          window.dispatchEvent(new CustomEvent("lead_studio:upgrade_required", { detail }));
        } catch {}
        // Module 6C.4: every existing call site has a catch block that
        // does `toast.error(err.response.data.detail || "Failed")`. With
        // the structured detail still in place, those toasts try to
        // render an object as a React child and crash. We replace the
        // detail with a short human string AFTER dispatching the event
        // (the modal already received the full object). Existing call
        // sites get a graceful fallback toast; nothing crashes.
        if (error.response && error.response.data) {
          error.response.data.detail = `Plan limit reached (${detail.label || detail.current_plan})`;
        }
      }
      return Promise.reject(error);
    },
  );
}
